import React from 'react';
import { Box, Button, ButtonGroup as MuiButtonGroup } from '@mui/material';
import { ButtonGroup as ButtonGroupType, FormButton } from './types';

interface ButtonGroupProps {
  config: ButtonGroupType;
  onSubmit?: (data: any) => void;
  onStepChange?: (currentStep: number, totalSteps: number) => void;
  currentStep?: number;
  totalSteps?: number;
  isMultiStep?: boolean;
  formData?: any;
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  config,
  onSubmit,
  onStepChange,
  currentStep = 1,
  totalSteps = 1,
  isMultiStep = false,
  formData,
}) => {
  const { buttons, position = 'right', layout = 'horizontal', spacing = 2, sx } = config;

  // Helper function to determine button type and variant
  const getButtonProps = (button: FormButton) => {
    let buttonType: 'submit' | 'button' | 'reset' = 'button';
    let buttonVariant: 'outlined' | 'contained' = 'contained';

    // Determine button type based on text content and position
    if (button.type) {
      buttonType = button.type;
    } else if (button.text.toLowerCase().includes('submit') || 
               button.text.toLowerCase().includes('save') || 
               button.text.toLowerCase().includes('next')) {
      buttonType = 'submit';
    } else if (button.text.toLowerCase().includes('reset') || 
               button.text.toLowerCase().includes('clear')) {
      buttonType = 'reset';
    }

    // Determine variant based on text content and position
    if (button.text.toLowerCase().includes('cancel') || 
        button.text.toLowerCase().includes('back') || 
        button.text.toLowerCase().includes('draft')) {
      buttonVariant = 'outlined';
    }

    return { buttonType, buttonVariant };
  };

  // Helper function to get button color
  const getButtonColor = (color?: string) => {
    const validColors = ['primary', 'secondary', 'error', 'warning', 'info', 'success'];
    return validColors.includes(color || '') ? color : 'primary';
  };

  // Handle button click
  const handleButtonClick = (button: FormButton) => {
    const { buttonType } = getButtonProps(button);
    
    if (button.onClick) {
      button.onClick();
    } else if (buttonType === 'submit' && onSubmit) {
      onSubmit(formData);
    } else if (isMultiStep && onStepChange) {
      if (button.text.toLowerCase().includes('next')) {
        onStepChange(currentStep + 1, totalSteps);
      } else if (button.text.toLowerCase().includes('back') || 
                 button.text.toLowerCase().includes('previous')) {
        onStepChange(currentStep - 1, totalSteps);
      }
    }
  };

  // Render individual button
  const renderButton = (button: FormButton, index: number) => {
    const { buttonType, buttonVariant } = getButtonProps(button);
    const buttonColor = getButtonColor(button.color);

    return (
      <Button
        key={index}
        type={buttonType}
        variant={buttonVariant}
        color={buttonColor as any}
        size={button.size || 'medium'}
        disabled={button.disabled}
        onClick={() => handleButtonClick(button)}
        sx={button.sx}
      >
        {button.text}
      </Button>
    );
  };

  // Determine container positioning
  const getContainerSx = () => {
    const baseSx = {
      display: 'flex',
      gap: spacing,
      ...sx,
    };

    switch (position) {
      case 'left':
        return { ...baseSx, justifyContent: 'flex-start' };
      case 'center':
        return { ...baseSx, justifyContent: 'center' };
      case 'right':
        return { ...baseSx, justifyContent: 'flex-end' };
      case 'space-between':
        return { ...baseSx, justifyContent: 'space-between' };
      default:
        return { ...baseSx, justifyContent: 'flex-end' };
    }
  };

  // Render buttons based on layout
  if (layout === 'vertical') {
    return (
      <Box sx={getContainerSx()}>
        {buttons.map((button, index) => (
          <Box key={index} sx={{ width: '100%' }}>
            {renderButton(button, index)}
          </Box>
        ))}
      </Box>
    );
  }

  // Horizontal layout with MUI ButtonGroup
  return (
    <Box sx={getContainerSx()}>
      <MuiButtonGroup variant="outlined" size="medium">
        {buttons.map((button, index) => renderButton(button, index))}
      </MuiButtonGroup>
    </Box>
  );
}; 